<template>
  <div class="caixa-resumo">
    <header class="caixa-resumo__header">
      <h2>
        <i class="fas fa-cash-register"></i> Resumo de Caixa
      </h2>
      <div class="caixa-resumo__actions">
        <button class="btn btn-light" @click="carregar({ atualizarConfiguracao: true })">
          <i class="fas fa-sync"></i> Atualizar
        </button>
        <button v-if="caixaAberto" class="btn btn-info" @click="abrirSuprimento">
          <i class="fas fa-arrow-down"></i> Suprimento
        </button>
        <button v-if="caixaAberto" class="btn btn-warning" @click="abrirSangria">
          <i class="fas fa-arrow-up"></i> Sangria
        </button>
        <button v-if="caixaAberto" class="btn btn-danger" @click="modalFechar = true">
          <i class="fas fa-lock"></i> Fechar Caixa
        </button>
        <button v-else-if="configuracaoGeral.abertura_caixa" class="btn btn-success" @click="modalAbrir = true">
          <i class="fas fa-lock-open"></i> Abrir Caixa
        </button>
      </div>
    </header>

    <section v-if="caixaAberto" class="caixa-resumo__status caixa-resumo__status--aberto">
      <span class="badge badge--aberto">Caixa aberto</span>
      <span>Aberto em: <b>{{ formatarData(caixaAberto.data_abertura) }}</b></span>
      <span>Valor inicial: <b>{{ ocultarValores ? '—' : brl(caixaAberto.valor_abertura) }}</b></span>
      <span v-if="resumo">Esperado: <b>{{ ocultarValores ? '—' : brl(resumo.valor_esperado) }}</b></span>
    </section>
    <section v-else-if="!configuracaoGeral.abertura_caixa" class="caixa-resumo__status caixa-resumo__status--fechado">
      <span class="badge badge--fechado">Controle de caixa desabilitado</span>
      <span>As vendas podem ser realizadas sem abertura de caixa.</span>
    </section>
    <section v-else class="caixa-resumo__status caixa-resumo__status--fechado">
      <span class="badge badge--fechado">Nenhum caixa aberto</span>
      <span>Abra um caixa para iniciar as vendas.</span>
    </section>

    <section class="caixa-resumo__table">
      <h3>Movimentações {{ uuidCaixaAtual ? '(' + uuidCaixaAtual.slice(0,8) + '…)' : '' }}</h3>

      <div v-if="carregando" class="caixa-resumo__loading">
        <span class="spinner-border spinner-border-sm mr-2"></span> Carregando…
      </div>

      <table v-else class="table table-sm">
        <thead>
          <tr>
            <th>Data/Hora</th>
            <th>Tipo</th>
            <th>Descrição</th>
            <th class="text-right">Valor</th>
            <th class="text-center">Impressão</th>
            <th class="text-center">Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="m in movimentos" :key="m.uuid" :class="linhaClasse(m)">
            <td>{{ formatarData(m.data_venda || m.data_abertura || m.data_fechamento || m.created_at) }}</td>
            <td><span class="badge" :class="badgeTipo(m.id_tipo)">{{ nomeTipo(m.id_tipo) }}</span></td>
            <td>{{ m.descricao || m.pagamento || '—' }}</td>
            <td class="text-right">{{ ocultarValores ? '—' : brl(m.valor_movimento || m.valor_abertura) }}</td>
            <td class="text-center">
              <span v-if="m.comprovante_impresso" class="badge badge-success">
                Impresso{{ m.reimpressao_count ? ' (+' + m.reimpressao_count + ')' : '' }}
              </span>
              <span v-else class="badge badge-warning">Não impresso</span>
            </td>
            <td class="text-center">
              <button class="btn btn-xs btn-light" :disabled="!temComprovante(m) || acao===m.uuid"
                      @click="visualizar(m)">
                <i class="fas fa-eye"></i>
              </button>
              <button class="btn btn-xs btn-primary" :disabled="!temComprovante(m) || acao===m.uuid"
                      @click="reimprimir(m)">
                <i class="fas fa-print"></i> Reimprimir
              </button>
            </td>
          </tr>
          <tr v-if="!movimentos.length">
            <td colspan="6" class="text-center text-muted py-3">Sem movimentações.</td>
          </tr>
        </tbody>
      </table>
    </section>

    <div v-if="visualizarHtml" class="comprovante-overlay" @click.self="fecharPreview">
      <div class="comprovante-box">
        <header>
          <b>Pré-visualização do comprovante</b>
          <div class="comprovante-box__actions">
            <button
              class="btn btn-xs btn-primary"
              :disabled="!visualizarUuid || acao === visualizarUuid"
              @click="imprimirPreview"
            >
              <i class="fas fa-print"></i> Imprimir
            </button>
            <button class="btn btn-xs btn-light" @click="fecharPreview">Fechar</button>
          </div>
        </header>
        <iframe :srcdoc="visualizarHtml"></iframe>
      </div>
    </div>

    <AbrirCaixaModal :visivel="modalAbrir" @fechar="modalAbrir = false" @aberto="onAberto" />
    <FecharCaixaModal :visivel="modalFechar" :caixa-aberto="caixaAberto"
                      :fechamento-as-cegas="configuracaoGeral.fechamento_as_cegas"
                      @fechar="modalFechar = false" @fechado="onFechado" />
    <MovimentoCaixaModal :visivel="modalMov.visivel" :tipo="modalMov.tipo"
                         @fechar="modalMov.visivel = false" @registrado="onMov" />
  </div>
</template>

<script>
import CaixaPdvDesktopService from "@/modules/PdvDesktop/services/CaixaPdvDesktopService";
import CaixaComprovantePrintService from "@/modules/PdvDesktop/services/CaixaComprovantePrintService";
import CaixaLocalRepository, { CAIXA_TIPO } from "@/modules/PdvDesktop/repositories/CaixaLocalRepository";
import AbrirCaixaModal from "@/modules/PdvDesktop/components/AbrirCaixaModal.vue";
import FecharCaixaModal from "@/modules/PdvDesktop/components/FecharCaixaModal.vue";
import MovimentoCaixaModal from "@/modules/PdvDesktop/components/MovimentoCaixaModal.vue";
import ConfiguracaoGeralPdvDesktopService from "@/modules/PdvDesktop/services/ConfiguracaoGeralPdvDesktopService";

const NOMES_TIPO = {
  [CAIXA_TIPO.ABERTURA]:   "Abertura",
  [CAIXA_TIPO.VENDA]:      "Venda",
  [CAIXA_TIPO.SANGRIA]:    "Sangria",
  [CAIXA_TIPO.SUPRIMENTO]: "Suprimento",
  [CAIXA_TIPO.FECHAMENTO]: "Fechamento",
};

export default {
  name: "CaixaResumo",
  components: { AbrirCaixaModal, FecharCaixaModal, MovimentoCaixaModal },
  data() {
    return {
      carregando: false,
      caixaAberto: null,
      uuidCaixaAtual: null,
      movimentos: [],
      resumo: null,
      acao: null,
      visualizarHtml: null,
      visualizarUuid: null,
      modalAbrir: false,
      modalFechar: false,
      modalMov: { visivel: false, tipo: "sangria" },
      configuracaoGeral: ConfiguracaoGeralPdvDesktopService.normalizar(),
    };
  },
  async mounted() {
    await this.carregar({ atualizarConfiguracao: true });
  },
  computed: {
    ocultarValores() {
      return this.configuracaoGeral.fechamento_as_cegas === true;
    },
  },
  methods: {
    brl(v) {
      const n = Number(v) || 0;
      return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    formatarData(iso) {
      if (!iso) return "—";
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? iso
        : d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
    },
    nomeTipo(t)  { return NOMES_TIPO[Number(t)] || t; },
    badgeTipo(t) {
      switch (Number(t)) {
        case CAIXA_TIPO.ABERTURA:   return "badge-success";
        case CAIXA_TIPO.VENDA:      return "badge-info";
        case CAIXA_TIPO.SANGRIA:    return "badge-warning";
        case CAIXA_TIPO.SUPRIMENTO: return "badge-primary";
        case CAIXA_TIPO.FECHAMENTO: return "badge-danger";
        default: return "badge-secondary";
      }
    },
    linhaClasse(m) {
      return Number(m.id_tipo) === CAIXA_TIPO.FECHAMENTO ? "row-fechamento" : "";
    },
    temComprovante(m) {
      return [
        CAIXA_TIPO.ABERTURA, CAIXA_TIPO.SANGRIA,
        CAIXA_TIPO.SUPRIMENTO, CAIXA_TIPO.FECHAMENTO,
      ].includes(Number(m.id_tipo));
    },

    async carregar({ atualizarConfiguracao = false } = {}) {
      this.carregando = true;
      try {
        if (atualizarConfiguracao) {
          this.configuracaoGeral = await ConfiguracaoGeralPdvDesktopService.carregarAtualizada();
        }
        this.caixaAberto = await CaixaPdvDesktopService.obterCaixaAberto();
        let uuid = this.caixaAberto ? this.caixaAberto.uuid_caixa : null;
        if (!uuid) {
          const pendentes = await CaixaLocalRepository.buscarPendentesSync({ apenasUsuarioAtual: true });
          uuid = pendentes && pendentes.length ? pendentes[pendentes.length - 1].uuid_caixa : null;
        }
        if (!uuid) {
          const ultimo = await CaixaLocalRepository.buscarUltimoCaixa();
          uuid = ultimo?.uuid_caixa || null;
        }
        this.uuidCaixaAtual = uuid;
        if (uuid) {
          this.movimentos = await CaixaLocalRepository.buscarMovimentosPorUuidCaixa(uuid);
          this.resumo     = this.caixaAberto
            ? await CaixaPdvDesktopService.calcularResumoCaixa(uuid)
            : null;
        } else {
          this.movimentos = [];
          this.resumo = null;
        }
      } finally {
        this.carregando = false;
      }
    },

    async reimprimir(m) {
      if (!this.temComprovante(m)) return;
      this.acao = m.uuid;
      try {
        const ret = await CaixaComprovantePrintService.reimprimirComprovanteCaixa(m.uuid);
        if (ret && ret.ok === false) {
          window.alert("Não foi possível imprimir: " + (ret.error || "erro desconhecido"));
        }
        await this.carregar();
      } finally {
        this.acao = null;
      }
    },

    async visualizar(m) {
      const preparado = await CaixaComprovantePrintService.prepararComprovanteCaixa(m.uuid);
      this.visualizarUuid = m.uuid;
      this.visualizarHtml = preparado.html || "<p>Sem comprovante.</p>";
    },

    fecharPreview() {
      this.visualizarHtml = null;
      this.visualizarUuid = null;
    },

    async imprimirPreview() {
      if (!this.visualizarUuid) return;
      const uuid = this.visualizarUuid;
      this.acao = uuid;
      try {
        const ret = await CaixaComprovantePrintService.reimprimirComprovanteCaixa(uuid);
        if (ret && ret.ok === false) {
          window.alert("Não foi possível imprimir: " + (ret.error || "erro desconhecido"));
        }
        await this.carregar();
      } finally {
        this.acao = null;
      }
    },

    abrirSangria()    { this.modalMov = { visivel: true, tipo: "sangria" }; },
    abrirSuprimento() { this.modalMov = { visivel: true, tipo: "suprimento" }; },

    async onAberto()  { this.modalAbrir = false; await this.carregar(); },
    async onFechado() { this.modalFechar = false; await this.carregar(); },
    async onMov()     { this.modalMov.visivel = false; await this.carregar(); },
  },
};
</script>

<style scoped>
.caixa-resumo { padding: 16px; }
.caixa-resumo__header {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 12px; gap: 12px; flex-wrap: wrap;
}
.caixa-resumo__header h2 { margin: 0; font-size: 1.2rem; }
.caixa-resumo__actions { display: flex; gap: 6px; flex-wrap: wrap; }
.caixa-resumo__status {
  display: flex; gap: 16px; align-items: center; flex-wrap: wrap;
  padding: 10px 14px; border-radius: 6px; margin-bottom: 12px;
}
.caixa-resumo__status--aberto  { background: #ecfdf5; border: 1px solid #a7f3d0; }
.caixa-resumo__status--fechado { background: #fef2f2; border: 1px solid #fecaca; }
.badge--aberto  { background: #047857; color: #fff; padding: 4px 10px; border-radius: 12px; }
.badge--fechado { background: #b91c1c; color: #fff; padding: 4px 10px; border-radius: 12px; }
.caixa-resumo__table h3 { font-size: 1rem; margin: 8px 0; }
.row-fechamento { background: #fef2f2; }
.btn-xs { padding: 2px 8px; font-size: 0.78rem; margin-right: 4px; }
.caixa-resumo__loading { padding: 24px; text-align: center; color: #6b7280; }

.comprovante-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.6);
  display: flex; align-items: center; justify-content: center; z-index: 5000;
}
.comprovante-box {
  background: #fff; border-radius: 8px; width: 90vw; max-width: 360px;
  height: 80vh; display: flex; flex-direction: column; overflow: hidden;
}
.comprovante-box header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 8px 12px; border-bottom: 1px solid #e5e7eb;
}
.comprovante-box__actions { display: flex; gap: 6px; }
.comprovante-box iframe { flex: 1; border: 0; background: #fff; }
</style>
